import { VodApi } from '../../../api/VodApi';
import { AssetType } from '../../../api/models/Shared.types';
import { VodDetailDescriptionDataSource } from './VodDetailDescriptionDataSource';
import { PriceApi } from '../../../api/price';
import { Log } from '@lightningjs/sdk';
import { VodStreamRights } from './VodStreamRights';
import { UserUtil } from '../../utility/UserUtil';
import { VodAsset } from './VodAsset';
import { SharedFavouriteActionButton } from '../../../pages/detail/actionRowButtons/SharedFavouriteActionButton';
import { DetailTranslationKeys, ErrorTranslationKeys, SettingsKeys } from '../../../api/models/TranslationKeys';
import { TabbedEpisodeRibbonDataSource } from '../../cms/TabbedEpisodeRibbonDataSource';
import { DetailDataSource } from '../DetailDataSource';
import { VodWatchAction } from '../actions/VodWatchAction';
import { ActionButton } from '../../../pages/detail/actionRowButtons/ActionButton';
import { VodBundleAction } from '../actions/VodBundleAction';
import { RentAction } from '../actions/RentAction';
import { ShowDetailAction } from '../actions/ShowDetailAction';
import { atvPromiseAll } from '../../utility/PromiseUtil';
import { WatchTrailerAction } from '../actions/WatchTrailerAction';
import { BookmarkUtility } from '../../utility/BookmarkUtility';
import { StaticPageUrls } from '../../../api/analytics/ClientEvents';
import { Backend } from '../../../api/Backend';
import { IActionButtonDefinition, IDetailModel, NextEpisodeInfo } from '../detail.types';
import { IAssetV5, IValidQuotePriceResponse3, IVodPriceResponse3 } from '../../../api/models/AndromeTV.types';
import { RibbonDataSource } from '../../cms/RibbonDataSource';

export class VodDetailDataSource extends DetailDataSource {
  constructor(backend: Backend) {
    super(backend);
  }

  override logPageView() {
    this.backend.logService.trackPageView(StaticPageUrls.vodDetail);
  }

  override load(id: string): Promise<IDetailModel> {
    return VodApi.asset(this.backend, id)
      .then(result => {
        const vod = new VodAsset(result);

        const quotePricing = this._fetchQuoteAndPricing(this.backend, vod.assetId);
        const nextEpisodeProm = this._checkNextEpi(this.backend, vod);

        return atvPromiseAll([quotePricing, nextEpisodeProm]).then(([vodStreamRights, nextEpisodeInfo]) => {
          const descrLogic = new VodDetailDescriptionDataSource(vod, AssetType.VOD, this.backend, vodStreamRights);
          const streamRights = vodStreamRights;
          const images = {
            detailPoster: vod.image.detail ? vod.image.detail : vod.image.poster,
          };
          const res: IDetailModel = {
            description: descrLogic,
            images: images,
            buttons: this.getButtons(this.backend, vod, streamRights, nextEpisodeInfo),
            ribbonDatasources: this.getRibbons(this.backend, vod),
          };
          return res;
        });
      })
      .catch(err => {
        Log.error('[VodDetailInitializer] error', err);
        throw this.backend.translations.forKey(ErrorTranslationKeys.error_general);
      });
  }

  _fetchQuoteAndPricing(backend: Backend, vodId: string): Promise<VodStreamRights> {
    const requests = [
      backend.cachedQuoteApi.list(backend),
      PriceApi.forVod(backend, vodId, {
        entitlementId: backend.sessionInfo?.device.entitlementId,
        catalogId: backend.deviceProfile?.profile.catalog,
      }),
    ];

    return Promise.all(requests).then(([quotes, price]) => {
      return new VodStreamRights(vodId, quotes as IValidQuotePriceResponse3[], price as IVodPriceResponse3);
    });
  }

  getButtons(
    backend: Backend,
    vod: VodAsset,
    streamRights: VodStreamRights,
    nextEpisodeInfo: NextEpisodeInfo
  ): IActionButtonDefinition[] {
    const result: IActionButtonDefinition[] = [];

    const showWatch = this.showVodWatchButton(backend, vod, streamRights, nextEpisodeInfo);
    const showRent = this.showRentButton(backend, vod, streamRights);
    if (backend.isAuthorized()) {
      if (showWatch) result.push(this.getWatchButton(backend, vod, streamRights, nextEpisodeInfo));

      if (!showWatch && showRent) result.push(this.getRentButton(backend, vod, streamRights));

      // if it is a series (has nextepisodeinfo) do not add bundle here, do later
      if (!nextEpisodeInfo && this.showBundleButton(backend, vod, streamRights))
        result.push(this.getBundleButton(backend, vod, streamRights));
    } else {
      if (showWatch) result.push(this.getWatchButton(backend, vod, streamRights, nextEpisodeInfo));
    }

    if (this.showTrailerButton(backend, vod, streamRights))
      result.push(this.getTrailerButton(backend, vod, streamRights));

    if (backend.isAuthorized()) {
      if (showWatch && showRent) result.push(this.getRentButton(backend, vod, streamRights));
      //add favourite button
      result.push(this.getFavouriteButton(backend, vod));
      //if it is a series (has nextepisodeinfo) add bundle here
      if (nextEpisodeInfo && this.showBundleButton(backend, vod, streamRights))
        result.push(this.getBundleButton(backend, vod, streamRights));
    }

    const seriesButton = this.getSeriesButton(backend, vod);
    if (this.showSeriesButton(backend, vod) && seriesButton) result.push(seriesButton);

    return result;
  }

  showRentButton(backend: Backend, vod: VodAsset, streamRights: VodStreamRights) {
    return UserUtil.rentingAllowed(backend) && !vod.isExpired() && streamRights.canRent();
  }

  /* eslint-disable no-unused-vars */
  getRentButton(backend: Backend, vod: VodAsset, streamRights: VodStreamRights): IActionButtonDefinition {
    const actionHandler = new RentAction(backend, AssetType.VOD, vod, streamRights);
    return {
      type: ActionButton,
      actionHandler,
      label: backend.translations.forKey(DetailTranslationKeys.detail_btn_rent_now),
    };
  }

  showVodWatchButton(backend: Backend, vod: VodAsset, streamRights: VodStreamRights, nextEpisodeInfo: NextEpisodeInfo) {
    const showSeriesWatchButton = backend.globalSettings.boolValue(SettingsKeys.showSeriesWatchButton);
    return (
      (streamRights && streamRights.canWatch() && !vod.isExpired()) ||
      // if vod is expired but you still have valid quote
      (streamRights && streamRights.hasWatchableOttTVOD() && vod.isExpired()) ||
      (!backend.isAuthorized() && !vod.isExpired()) ||
      ((showSeriesWatchButton ? showSeriesWatchButton : false) &&
        nextEpisodeInfo &&
        nextEpisodeInfo.streamRights &&
        ((nextEpisodeInfo.streamRights.canWatch() && !nextEpisodeInfo.asset.isExpired()) ||
          (nextEpisodeInfo.streamRights.hasWatchableOttTVOD() && nextEpisodeInfo.asset.isExpired())))
    );
  }

  getWatchButton(
    backend: Backend,
    vod: VodAsset,
    streamRights: VodStreamRights,
    nextEpisodeInfo: NextEpisodeInfo
  ): IActionButtonDefinition {
    const selectedVod = nextEpisodeInfo ? nextEpisodeInfo.asset : vod;
    const selectedRights = nextEpisodeInfo ? nextEpisodeInfo.streamRights : streamRights;
    const actionHandler = new VodWatchAction(backend, selectedVod, selectedRights);
    return {
      type: ActionButton,
      actionHandler,
      label: backend.translations.forKey(DetailTranslationKeys.detail_btn_watch_now),
    };
  }

  showTrailerButton(backend: Backend, vod: VodAsset, streamRights: VodStreamRights) {
    return streamRights.hasTrailer() && !vod.isExpired();
  }

  getTrailerButton(backend: Backend, vod: VodAsset, streamRights: VodStreamRights): IActionButtonDefinition {
    const actionHandler = new WatchTrailerAction(backend, streamRights.trailerStreams, vod);
    return {
      type: ActionButton,
      actionHandler,
      label: backend.translations.forKey(DetailTranslationKeys.detail_btn_trailer),
    };
  }

  showBundleButton(backend: Backend, vod: VodAsset, streamRights: VodStreamRights) {
    return streamRights.hasBundles() && !vod.isExpired();
  }

  getBundleButton(backend: Backend, vod: VodAsset, streamRights: VodStreamRights): IActionButtonDefinition {
    const actionHandler = new VodBundleAction(backend, '', streamRights);
    return {
      type: ActionButton,
      actionHandler,
      label: backend.translations.forKey(DetailTranslationKeys.detail_btn_bundle),
    };
  }

  showSeriesButton(backend: Backend, vod: VodAsset) {
    return vod.seriesId;
  }

  getSeriesButton(backend: Backend, vod: VodAsset): IActionButtonDefinition | undefined {
    if (!vod.seriesId) return;
    const actionHandler = new ShowDetailAction(backend, vod.seriesId, AssetType.VOD);
    return {
      type: ActionButton,
      actionHandler,
      label: backend.translations.forKey(DetailTranslationKeys.detail_btn_series),
    };
  }

  getFavouriteButton(backend: Backend, vod: VodAsset): IActionButtonDefinition {
    return {
      type: SharedFavouriteActionButton,
      assetId: vod.assetId,
      assetType: AssetType.VOD,
      assetIsAdult: vod.adult,
    };
  }
  getRibbons(backend: Backend, asset: VodAsset) {
    let ribbonDataSources: RibbonDataSource[] = [];

    const episodeDs = this.createEpisodesDataSource(backend, asset);
    if (episodeDs) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ribbonDataSources.push(episodeDs);
    }
    if (asset.seriesId) {
      ribbonDataSources = ribbonDataSources.concat(
        this.createRelatedDataSources(backend, AssetType.SERIES, asset.seriesId, false)
      );
    } else {
      ribbonDataSources = ribbonDataSources.concat(
        this.createRelatedDataSources(backend, asset.assetType, asset.assetId, false)
      );
    }

    return ribbonDataSources;
  }

  createEpisodesDataSource(backend: Backend, asset: VodAsset): TabbedEpisodeRibbonDataSource | undefined {
    if (asset.assetType !== AssetType.SERIES && !asset.seriesId) {
      return undefined;
    }
    // if asset is part of series (has seriesId) then use this to get all seasons/episodes
    return new TabbedEpisodeRibbonDataSource(
      backend,
      asset.seriesId || asset.assetId,
      backend.translations.forKey(DetailTranslationKeys.detail_btn_all_episodes)
    );
  }

  _checkNextEpi(backend: Backend, vod: VodAsset): Promise<NextEpisodeInfo | undefined> {
    // Get bookmark met assetId == seriesId van bookmark, sorteer op nieuw -> oud
    // als er bookmarks zijn, neem dan de nieuwste.
    // is deze nog niet volledig uit bekeken : deze id nieuwe vod call voor asset te bekomen
    // wel volledig uit gekeken -> doe next-asset call
    // geen bookmarks: doe season call, doe episode call van eerste season en gebruik eerste aflevering;
    const showSeriesButton = backend.globalSettings.boolValue(SettingsKeys.showSeriesWatchButton);
    if (!(showSeriesButton ? showSeriesButton : false)) return Promise.resolve(undefined);

    if (vod.assetType === AssetType.SERIES && backend.isAuthorized()) {
      return backend.cachedBookmarkApi
        .seriesBookmarks(backend, vod.assetId, AssetType.VOD)
        .then(bookmarks => {
          const latest = BookmarkUtility.getLatestBookmark(bookmarks);

          if (bookmarks && bookmarks.length === 0) {
            // there are no bookmarks found for this serie
            return this.getFirstEpisode(backend, vod);
          }

          if (latest && BookmarkUtility.isFullyWatched(backend, latest)) {
            // the latest bookmark is fully watched so use nex-asset to get new episode
            return VodApi.nextAsset(backend, latest.id, backend.deviceProfile?.profile.catalog).then(res => {
              if (res.episode) {
                return res.episode;
              } else {
                return this.getFirstEpisode(backend, vod);
              }
            });
          } else if (latest) {
            // latest bookmark is not yet completed so use this to watch, do vod call to get asset
            return VodApi.asset(backend, latest.id);
          }
          return undefined;
        })
        .then(selectedEpisode => {
          if (selectedEpisode) {
            return this._getEpisode(backend, selectedEpisode);
          }
          return undefined;
        });
    }
    return Promise.resolve(undefined);
  }

  private getFirstEpisode(backend: Backend, vod: VodAsset): Promise<IAssetV5 | undefined> {
    // do season call to get all the seasons
    return VodApi.assetSeasons(backend, vod.assetId)
      .then(seasons => {
        seasons.sort((a, b) => {
          if (!a.seasonNumber || !b.seasonNumber) return 0;
          if (a.seasonNumber < b.seasonNumber) {
            return 1;
          } else if (a.seasonNumber > b.seasonNumber) {
            return -1;
          } else {
            return 0;
          }
        });
        const firstSeason = seasons[0];
        if (firstSeason && firstSeason.seasonId) {
          // do episode call
          return VodApi.seasonEpisodes(backend, firstSeason.seasonId).then(episodes => {
            return episodes[0];
          });
        } else {
          return undefined;
        }
      })
      .catch(() => {
        return undefined;
      });
  }

  _getEpisode(backend: Backend, selectedEpisode: IAssetV5): Promise<NextEpisodeInfo> {
    return this._fetchQuoteAndPricing(backend, selectedEpisode.assetId).then(r => {
      return {
        asset: new VodAsset(selectedEpisode),
        streamRights: r,
      };
    });
  }
}
